import { useEffect, useState } from "react";
import { X, Download } from "lucide-react";
import { Button } from "./ui/button";

const DISMISS_KEY = "boardroom_pwa_install_dismissed_at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 14; // hide for 14 days after dismiss
const EARNED_KEY = "boardroom_first_session_done";
const SHOWN_KEY = "boardroom_pwa_install_shows";
const MAX_SHOWS = 3;

/**
 * Records that this device has a session on the books — the moment the install
 * nudge becomes welcome rather than pushy. Safe to call repeatedly.
 */
export function markFirstSessionDone() {
  try {
    localStorage.setItem(EARNED_KEY, "1");
  } catch (e) {
    console.debug("markFirstSessionDone: localStorage write failed", e);
  }
}

const isPhone = () =>
  window.matchMedia("(max-width: 767px)").matches ||
  window.matchMedia("(pointer: coarse)").matches;

const earned = () => {
  try {
    return localStorage.getItem(EARNED_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * Discreet install banner — phone only, and only once the user has their first
 * session on file (`markFirstSessionDone()`). Android/Chrome uses the native
 * `beforeinstallprompt` event; iOS Safari gets a manual "Add to Home Screen"
 * hint. Capped at three appearances, 14 days apart after a dismiss.
 */
export default function InstallPrompt() {
  const [deferredEvt, setDeferredEvt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState("chrome"); // "chrome" | "ios"

  useEffect(() => {
    // Already installed → never show.
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (standalone) return;

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;

    // Phone-only, and only after the first session is on the books.
    if (!isPhone() || !earned()) return;
    if (Number(localStorage.getItem(SHOWN_KEY) || 0) >= MAX_SHOWS) return;

    const reveal = (p) => {
      setPlatform(p);
      setVisible(true);
      try {
        localStorage.setItem(SHOWN_KEY, String(Number(localStorage.getItem(SHOWN_KEY) || 0) + 1));
      } catch (e) {
        console.debug("InstallPrompt: show-count write failed", e);
      }
    };

    // Android / mobile Chrome path
    let chromeTimer;
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredEvt(e);
      chromeTimer = setTimeout(() => reveal("chrome"), 3000);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari path — no event; sniff and show a manual hint after a short delay.
    const ua = window.navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
    if (isIOS && isSafari) {
      const t = setTimeout(() => reveal("ios"), 3000);
      return () => {
        clearTimeout(t);
        clearTimeout(chromeTimer);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => {
      clearTimeout(chromeTimer);
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferredEvt) return;
    deferredEvt.prompt();
    try {
      await deferredEvt.userChoice;
    } catch (e) {
      // Prompt closed with no choice; treat as dismiss.
      console.debug("InstallPrompt: userChoice rejected", e);
    }
    setDeferredEvt(null);
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-4 right-4 md:left-auto md:right-4 md:bottom-4 md:w-[380px] bottom-20 z-[80] rounded-lg border border-[#c89f45]/40 bg-[#121212] shadow-[0_20px_50px_rgba(0,0,0,0.6)] p-4"
      data-testid="pwa-install-banner"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-md bg-[#0a0a0a] border border-[#c89f45] flex items-center justify-center">
          <Download className="w-4 h-4 text-[#c89f45]" strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <p className="text-[11px] tracking-[3px] uppercase text-[#c89f45] font-medium mb-1">Install Boardroom</p>
          {platform === "chrome" ? (
            <p className="text-sm text-[#ddd] leading-snug">
              Add to your home screen — one tap into every board meeting.
            </p>
          ) : (
            <p className="text-sm text-[#ddd] leading-snug">
              Tap <span className="text-[#c89f45]">Share</span> in Safari, then <span className="text-[#c89f45]">&ldquo;Add to Home Screen&rdquo;</span>.
            </p>
          )}
          {platform === "chrome" && (
            <div className="mt-3 flex items-center gap-2">
              <Button
                onClick={install}
                className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] text-xs h-8 rounded-md px-3"
                data-testid="pwa-install-accept"
              >
                Install
              </Button>
              <Button
                onClick={dismiss}
                variant="outline"
                className="bg-transparent border-[#2a2a2a] text-[#aaa] hover:bg-[#1a1a1a] hover:text-white text-xs h-8 rounded-md px-3"
                data-testid="pwa-install-later"
              >
                Later
              </Button>
            </div>
          )}
        </div>
        <button
          onClick={dismiss}
          className="text-[#666] hover:text-[#ddd] transition-colors"
          aria-label="Dismiss install prompt"
          data-testid="pwa-install-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
